<template>
  <div class="container" v-if="campaignStore.currentCampaign">
    <Header
      :name="campaignStore.currentCampaign.name"
      :isPublic="campaignStore.currentCampaign.invitation_id"
      :shareLink="campaignStore.campaignShareLink"
      :buttons="[
        {
          class: 'outline secondary icon icon-left',
          label: 'Paramètres de la campagne',
          icon: 'bi-gear',
          disabled: campaignStore.currentCampaign.archived,
          click: () => {
            state.showParameters = true;
          },
        },
        {
          class: 'primary icon icon-left',
          label: 'Retourner au tableau de bord',
          icon: 'bi-arrow-left',
          disabled: campaignStore.currentCampaign.archived,
          routeParams: {
            name: 'campaign',
          },
        },
      ]"
    />
    <div class="share-card card" data-step="7.1">
      <div class="inline" @click="showLinkData">
        <h4>Inviter les talents par un lien</h4>
        <div class="toggle-container">
          <div class="toggle" @click="showCampaignLinkModal">
            <input type="checkbox" :checked="state.linkActivated" />
            <label />
          </div>
        </div>
        <i
          class="bi bi-chevron-down"
          :class="{ open: state.showLink }"
          v-if="campaignStore.currentCampaign.invitation_id"
        />
      </div>
      <div class="link-text">
        Utilisez un lien pour permettre aux talents de rejoindre votre campagne
        en un seul clic, sans inscription manuelle de votre part. Vous pouvez le
        partager sur des plateformes de recrutement, vos réseaux sociaux ou par
        e-mail. Les options de gestion de ce lien sont modifiables à tout moment
        dans les paramètres de votre campagne.
      </div>
      <transition name="fade">
        <div v-if="state.showLink && state.linkActivated">
          <InputCopy
            class="link-title"
            label="Lien de la campagne"
            :value="campaignStore.campaignShareLink"
          />
          <div data-step="7.2">
            <h4 class="link-title">Activation des talents</h4>
            <div class="link-text">
              Définissez comment vos talents accèdent à leur évaluation
              lorsqu'ils rejoignent votre campagne. Cette option s'applique
              uniquement aux talents invités par lien.
            </div>
            <div class="radio-group">
              <label
                :class="[
                  'radio-option',
                  'radio-option-left',
                  state.autoActivateCandidate ? 'radio-option-selected' : '',
                ]"
              >
                <input
                  class="auto-radio-button-dot"
                  type="radio"
                  :checked="state.autoActivateCandidate"
                  @change="handleRadioChange(true)"
                />
                <div>
                  <div class="radio-option-title">Activer automatiquement</div>
                  <div class="radio-option-text">
                    Vos talents peuvent passer leur évaluation dès qu'ils créent
                    leur compte. Un crédit sera automatiquement déduit. Vous
                    fluidifier leur expérience talent.
                  </div>
                </div>
              </label>
              <label
                :class="[
                  'radio-option',
                  !state.autoActivateCandidate ? 'radio-option-selected' : '',
                ]"
              >
                <input
                  class="auto-radio-button-dot"
                  type="radio"
                  :checked="!state.autoActivateCandidate"
                  @change="handleRadioChange(false)"
                />
                <div>
                  <div class="radio-option-title">Activer manuellement</div>
                  <div class="radio-option-text">
                    Vos talents créent leur compte et attendent votre
                    validation. Activez leur évaluation manuellement pour
                    déduire un crédit. Vous avez le contrôle.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </transition>
    </div>
    <div class="divider">
      <div class="divider-line" />
      <div>Ou</div>
      <div class="divider-line" />
    </div>
    <div class="form-card card" data-step="7.3">
      <h4>Inviter les talents par e-mail</h4>
      <VForm
        :button="{
          label: 'Ajouter le talent',
          class: 'primary',
          hotDisabled: true,
        }"
        :externalRule="state.email !== ''"
        :displayErrors="false"
        :inline="true"
        :callback="addParticipantToList"
        :clean="true"
      >
        <template #form-body="{ errors }">
          <VField
            label="Adresse e-mail du talent*"
            name="add-candidate-email"
            placeholder="adresse@email.com"
            type="email"
            v-model="state.email"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-email'),
            }"
            :rules="{
              email: true,
              not_included: state.newParticipants.map((elt) => elt.email),
            }"
            :displayError="true"
          />
          <VField
            label="Prénom du talent"
            placeholder="John"
            name="add-candidate-firstname"
            v-model="state.firstname"
            rules="textOnly"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-firstname'),
            }"
            :displayError="true"
          />
          <VField
            label="Nom du talent"
            name="add-candidate-lastname"
            placeholder="Doe"
            v-model="state.lastname"
            rules="textOnly"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-lastname'),
            }"
            :displayError="true"
          />
        </template>
      </VForm>
    </div>
    <div class="data-card card">
      <h4>Liste des talents ajoutés</h4>
      <p>
        Retrouvez tous les talents ajoutés dans cette campagne et leurs
        informations.
      </p>
      <Table :fields="state.fields" :data="state.newParticipants">
        <template #first_name="{ data }">
          <p v-if="!data.first_name" class="muted">Non renseigné</p>
          <template v-else>{{ data.first_name }}</template>
        </template>
        <template #last_name="{ data }">
          <p v-if="!data.last_name" class="muted">Non renseigné</p>
          <template v-else>{{ data.last_name }}</template>
        </template>
        <template #actions="actionsProps">
          <Button
            label="Supprimer le talent"
            class="red outline icon icon-left"
            icon="bi-trash"
            @click="removeFromList(actionsProps.row.email)"
          />
        </template>
        <template #empty>
          <Empty
            message="<b>Aucun talent</b> dans la liste d'ajout pour le moment"
            :dark="isDark"
            icon="loop"
          />
        </template>
      </Table>
      <Button
        class="primary"
        label="Terminer l'ajout et retourner au tableau de bord"
        :disabled="state.newParticipants.length < 1"
        :loading="state.isLoading"
        @click="addParticipantsToCampaign"
      />
    </div>
    <ParametersModal
      :isOpen="state.showParameters"
      :campaign="campaignStore.currentCampaign"
      @close="state.showParameters = !state.showParameters"
    />
    <ActionModal
      modal="activate"
      :data="state.participants"
      :isOpen="state.showActivateModal"
      @close="cancelActivateModal"
      @confirm="cancelActivateModal"
    />
    <ActionModal
      modal="deactivateLink"
      :data="[]"
      :isOpen="state.showLinkModal"
      @close="state.showLinkModal = false"
      @confirm="enableCampaignLink"
    />
  </div>
</template>

<script setup>
import { reactive, watch, onMounted } from "vue";
import { useAppStore } from "@/stores/app.js";
import { useParticipantStore } from "@/stores/participant.js";
import { useCampaignStore } from "@/stores/campaign.js";
import { useCompanyStore } from "@/stores/company.js";
import { useOnboardingStore } from "@/stores/onboarding.js";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { useDark } from "@vueuse/core";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import Header from "@/components/Header.vue";
import Empty from "@/components/table/Empty.vue";
import ParametersModal from "@/components/modals/app/parametersModal/ParametersModal.vue";
import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";

const isDark = useDark();

const route = useRoute();

const router = useRouter();

const toast = useToast();

// Init stores
const appStore = useAppStore();
const { addParticipants, activateParticipants } = useParticipantStore();
const campaignStore = appStore.isOnboarded
  ? useCampaignStore()
  : useOnboardingStore();
const companyStore = useCompanyStore();

if (!campaignStore.currentCampaign) {
  await campaignStore.getCampaign(
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId
  );
}

// For onboarding
var { showCampaignLinkInfo } = storeToRefs(appStore);

//onMounted
onMounted(() => {
  if (!appStore.isOnboarded) {
    // In on before mount need to use .value for some reason
    handleOnboardingLinkInfo(showCampaignLinkInfo.value);

    watch(showCampaignLinkInfo, (newValue) => {
      handleOnboardingLinkInfo(newValue);
    });
  }
});

function handleOnboardingLinkInfo(value) {
  if (value) {
    campaignStore.currentCampaign.invitation_id = "/";
    campaignStore.campaignShareLink =
      "game.yuzu.hr/join?invitation_id=abcdefgh-1234-5678-a1b2-987654321yz";
    state.autoActivateCandidate = true;
    state.linkActivated = true;
    state.showLink = true;
  } else {
    state.showLink = false;
    state.autoActivateCandidate = false;
    state.linkActivated = false;
    campaignStore.currentCampaign.invitation_id = null;
    campaignStore.campaignShareLink = null;
  }
}

const state = reactive({
  email: "",
  firstname: "",
  lastname: "",
  activateModalIsLoading: false,
  isLoading: false,
  showLink: false,
  autoActivateCandidate: campaignStore.currentCampaign.auto_activate,
  linkActivated: campaignStore.currentCampaign.invitation_id ? true : false,
  showParameters: false,
  showActivateModal: false,
  showLinkModal: false,
  newParticipants: [],
  participants: [],
  fields: [
    {
      id: "first_name",
      label: "Prénom",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "last_name",
      label: "Nom",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "email",
      label: "Adresse e-mail",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
  ],
});

function addParticipantToList() {
  state.newParticipants.unshift({
    email: state.email,
    first_name: state.firstname,
    last_name: state.lastname,
    activeAssessment: true,
  });
}

function removeFromList(email) {
  const entry = state.newParticipants.findIndex((elt) => elt.email === email);

  state.newParticipants.splice(entry, 1);
}

async function addParticipantsToCampaign() {
  state.isLoading = true;

  // If not enough credits do not create participants
  // Just display warning message
  if (
    state.newParticipants.length > companyStore.currentCompany.credits.available
  ) {
    state.participants = state.newParticipants;
    state.showActivateModal = true;
    state.isLoading = false;
    return;
  }

  // Create participants
  const { participants } = await addParticipants(
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId,
    state.newParticipants
  );

  // Activate directly participants
  const participant_ids = participants.map((elt) => elt.participant_id);

  if (participant_ids.length > 0) {
    // avoid error if participants already added
    const resp = await activateParticipants(
      route.params.companyId,
      route.params.spaceId,
      route.params.campaignId,
      participant_ids
    );

    if (resp.code) {
      toast.error(getError(resp.key));
    } else {
      toast.success(
        `Talent${participant_ids.length > 1 ? "s" : ""} ajouté${
          participant_ids.length > 1 ? "s" : ""
        } avec succès.`
      );
      appStore.activatedTableHasNews[`${route.params.campaignId}`] = true;
    }
  }
  goBack();

  state.isLoading = false;
}

function cancelActivateModal() {
  state.showActivateModal = false;
}

function goBack() {
  appStore.currentCampaignTab = "Finished";

  state.newParticipants = [];

  router.push({ name: "campaign" });
}

function showLinkData() {
  if (!state.isLoading && state.linkActivated && !state.showLinkModal) {
    state.showLink = !state.showLink;
  }
}

function showCampaignLinkModal(event) {
  if (state.linkActivated) {
    event.preventDefault();
    state.showLinkModal = true;
  } else {
    enableCampaignLink();
  }
}

async function enableCampaignLink() {
  state.linkActivated = !state.linkActivated;
  state.isLoading = true;
  await campaignStore.updateCampaignInvitation(
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId,
    state.linkActivated
  );
  state.isLoading = false;
  campaignStore.currentCampaign.auto_activate = true;
  state.autoActivateCandidate = true;
  state.showLink = state.linkActivated;

  state.showLinkModal = false;
}

async function handleRadioChange(value) {
  state.autoActivateCandidate = value;
  campaignStore.currentCampaign.auto_activate = value;

  await campaignStore.updateCampaign(
    route.params.companyId,
    route.params.spaceId,
    campaignStore.currentCampaign
  );
}
</script>
